/**
 * Signed heartbeat chain (DESIGN.md §8.3) — the game-agnostic anti-cheat protocol shared by
 * the client SDK and the API Worker so the two can never disagree on the format.
 *
 * During play the client emits periodic checkpoints; each carries a rolling HMAC that chains
 * to the previous one, rooted at the session. Keyed by the per-session **token** (which both
 * sides possess: the server issues it and can recompute it). The chain makes it impossible to
 * drop, reorder, or fabricate a subset of beats after the fact without the session secret.
 *
 * All crypto is WebCrypto (`crypto.subtle`) — available in Workers, browsers, and Node 18+.
 */

const VERSION = "arcade-hb-v1";
const encoder = new TextEncoder();

export interface Heartbeat {
  seq: number; // 0-based, strictly increasing by 1
  elapsedMs: number; // ms since session start; non-decreasing
  scoreSoFar: number; // score at this beat; non-decreasing
  mac: string; // base64url HMAC chaining to the previous beat
}

/** A checkpoint before it is signed. */
export interface HeartbeatPoint {
  elapsedMs: number;
  scoreSoFar: number;
}

function bytesToBase64Url(bytes: Uint8Array): string {
  let binary = "";
  for (const b of bytes) binary += String.fromCharCode(b);
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

async function hmac(key: string, message: string): Promise<string> {
  const cryptoKey = await crypto.subtle.importKey(
    "raw",
    encoder.encode(key),
    { name: "HMAC", hash: "SHA-256" },
    false,
    ["sign"],
  );
  const sig = await crypto.subtle.sign("HMAC", cryptoKey, encoder.encode(message));
  return bytesToBase64Url(new Uint8Array(sig));
}

/** Length-independent-leak-resistant string compare. */
function timingSafeEqual(a: string, b: string): boolean {
  const ab = encoder.encode(a);
  const bb = encoder.encode(b);
  let diff = ab.length ^ bb.length;
  const len = Math.max(ab.length, bb.length);
  for (let i = 0; i < len; i++) diff |= (ab[i] ?? 0) ^ (bb[i] ?? 0);
  return diff === 0;
}

/** The MAC seeding the chain (prev for seq 0), bound to the session id. */
export function rootMac(key: string, sessionId: string): Promise<string> {
  return hmac(key, `${VERSION}|root|${sessionId}`);
}

function beatMessage(prevMac: string, p: HeartbeatPoint & { seq: number }): string {
  return `${VERSION}|${prevMac}|${p.seq}|${p.elapsedMs}|${p.scoreSoFar}`;
}

/** Build a full signed chain from ordered checkpoints (client side). */
export async function buildChain(
  key: string,
  sessionId: string,
  points: HeartbeatPoint[],
): Promise<Heartbeat[]> {
  let prev = await rootMac(key, sessionId);
  const beats: Heartbeat[] = [];
  for (let i = 0; i < points.length; i++) {
    const p = points[i]!;
    const beat = { seq: i, elapsedMs: p.elapsedMs, scoreSoFar: p.scoreSoFar };
    const mac = await hmac(key, beatMessage(prev, beat));
    beats.push({ ...beat, mac });
    prev = mac;
  }
  return beats;
}

export interface VerifyResult {
  ok: boolean;
  reason?: string;
  finalScore?: number;
  durationMs?: number;
}

/**
 * Verify a heartbeat chain (server side). Checks: non-empty; `seq` is 0..n-1; `elapsedMs` and
 * `scoreSoFar` non-decreasing; every MAC chains correctly. Returns the final score and
 * duration for the caller's plausibility checks.
 */
export async function verifyChain(
  key: string,
  sessionId: string,
  beats: unknown,
): Promise<VerifyResult> {
  if (!Array.isArray(beats) || beats.length === 0) {
    return { ok: false, reason: "empty" };
  }
  if (beats.length > 10_000) return { ok: false, reason: "too_many_beats" };
  let prev = await rootMac(key, sessionId);
  let lastElapsed = -1;
  let lastScore = -1;
  for (let i = 0; i < beats.length; i++) {
    const b = beats[i] as Partial<Heartbeat>;
    if (b?.seq !== i || !Number.isSafeInteger(b.seq)) return { ok: false, reason: "bad_seq" };
    if (typeof b.elapsedMs !== "number" || !Number.isSafeInteger(b.elapsedMs) || b.elapsedMs < 0 || b.elapsedMs < lastElapsed) {
      return { ok: false, reason: "non_monotonic_time" };
    }
    if (typeof b.scoreSoFar !== "number" || !Number.isSafeInteger(b.scoreSoFar) || b.scoreSoFar < 0 || b.scoreSoFar < lastScore) {
      return { ok: false, reason: "non_monotonic_score" };
    }
    if (typeof b.mac !== "string") return { ok: false, reason: "missing_mac" };
    const expected = await hmac(
      key,
      beatMessage(prev, { seq: i, elapsedMs: b.elapsedMs, scoreSoFar: b.scoreSoFar }),
    );
    if (!timingSafeEqual(expected, b.mac)) return { ok: false, reason: "bad_mac" };
    prev = b.mac;
    lastElapsed = b.elapsedMs;
    lastScore = b.scoreSoFar;
  }
  const last = beats[beats.length - 1] as Heartbeat;
  return { ok: true, finalScore: last.scoreSoFar, durationMs: last.elapsedMs };
}
